import { Menu, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'
import { Tooltip } from 'react-tooltip'

import ButtonDown300 from '../../../assets/images/icons/button-down-icon-300.svg?react'
import ButtonUp200 from '../../../assets/images/icons/button-up-icon-200.svg?react'
import ExportIcon from '../../../assets/images/icons/export-icon.svg?react'
import ExportSpinner from '../../../assets/Loaders/spin/export-spinner.svg?react'
import { useStartExportMutation } from '../../../store/api/product'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { setExportStatus } from '../../../store/slices/settingsSlice'
import { IntercomEvent } from '../../../utils/helpers/getIntercomMsg'
import useIntercomInternal from '../../../utils/hooks/use-intercom-internal'

export type ExportButtonProps = {
  options: { text: string; value: string }[]
}

export default function ExportButton({ options }: ExportButtonProps) {
  const dispatch = useAppDispatch()
  const canExport = useAppSelector((state) => !state.settings.is_export_ongoing)
  const [exportMutation, { isLoading: isExportLoading }] =
    useStartExportMutation()

  const [isExportOpen, setExportOpen] = useState(false)
  const { trackEventInternal } = useIntercomInternal()

  let extraProps = {}
  if (!canExport) {
    extraProps = {
      'data-tooltip-id': 'export-button',
      'data-tooltip-place': 'bottom',
    }
  }
  return (
    <>
      <Tooltip id="export-button" content="An export is already going on." />
      <Menu
        as="div"
        className="relative text-left pr-3  sm:pt-0 w-full sm:w-min"
      >
        <div>
          <Menu.Button
            disabled={!canExport}
            onClick={() => {
              if (canExport) setExportOpen((current) => !current)
            }}
            className={`flex flex-row items-center justify-center px-3 lg:px-4 py-2.5 border border-solid rounded-lg border-gray-300 w-full disabled:opacity-75`}
            {...extraProps}
          >
            <ExportIcon className="mr-2" />
            <span className="mr-2">Export</span>
            {canExport ? (
              isExportOpen ? (
                <ButtonUp200 />
              ) : (
                <ButtonDown300 />
              )
            ) : (
              <ExportSpinner />
            )}
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute lg:right-0 mt-1 w-56 origin-top-right divide-y divide-gray-200 rounded-md bg-white-100 shadow-lg  z-10">
            <div className="px-1 py-1">
              {options.map((option) => (
                <Menu.Item key={option.value}>
                  <button
                    onClick={async () => {
                      if (!isExportLoading) {
                        try {
                          await exportMutation({
                            export_type: option.value,
                          }).unwrap()
                          dispatch(
                            setExportStatus({
                              is_export_ongoing: true,
                            }),
                          )
                          trackEventInternal(
                            IntercomEvent['export-requested'],
                            {
                              'Export Type': option.value,
                            },
                          )
                        } catch (error) {
                          trackEventInternal(
                            IntercomEvent['export-request-failed'],
                            {
                              'Export Type': option.value,
                            },
                          )
                        }
                      }
                    }}
                    className=" text-gray-900 hover:bg-gray-50 group flex w-full items-center rounded-md px-2 py-2 text-sm"
                  >
                    <span className="font-normal text-sm text-gray-700 pl-3">
                      {option.text}
                    </span>
                  </button>
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </>
  )
}
